import React, { useMemo, useState } from 'react'
import Button from 'components/ui/Button'
import TextField from 'components/ui/TextField'
import Modal from 'react-modal'
import { cn } from 'cn'
import {
  PromptDialogContext,
  PromptDialogArgs,
  PromptOption
} from 'components/PromptDialog/context'

export { PromptDialogContext } from 'components/PromptDialog/context'
export type {
  PromptDialogArgs,
  PromptOption
} from 'components/PromptDialog/context'

type NormalizedOption = { label: string; value: string }

const normalizeOption = (option: PromptOption): NormalizedOption => {
  if (typeof option === 'string') {
    return { label: option, value: option }
  }
  return option
}

type PromptDialogCallbacks = {
  onCancel: () => void
  onSuccess: (value: string) => void
}

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderWidth: 0,
    margin: 0,
    padding: 0,
    overflow: 'hidden'
  },
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.53)',
    zIndex: 1000
  }
}

type PromptDialogProps = PromptDialogArgs & PromptDialogCallbacks

function PromptDialog({
  title = 'Are you sure?',
  message,
  okButton = 'Confirm',
  cancelButton = 'Cancel',
  color = 'primary',
  fieldLabel,
  placeholder,
  options,
  onCancel,
  onSuccess
}: PromptDialogProps) {
  const [value, setValue] = useState<string>('')

  const hasOptions = options && options.length > 0
  const normalizedOptions: NormalizedOption[] = hasOptions
    ? options.map(normalizeOption)
    : []

  return (
    <Modal onRequestClose={() => onCancel()} style={modalStyles} isOpen>
      <div className="rounded-lg text-left overflow-hidden shadow-xl max-w-lg w-full min-w-[90vw] md:min-w-fit">
        <div className="bg-white px-6 pt-6 pb-5 sm:p-8 sm:pb-6">
          <div className="flex items-start">
            <div className="mt-3 sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {title}
              </h3>
              <div className="mt-4 mb-4">
                <p className="text-sm text-gray-500">{message}</p>
              </div>
              {hasOptions ? (
                <fieldset>
                  {fieldLabel && (
                    <legend className="text-base font-medium text-gray-700 mb-2">
                      {fieldLabel}
                    </legend>
                  )}
                  <div className="space-y-2">
                    {normalizedOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          checked={value === opt.value}
                          className="rounded-full border-gray-300 text-orange-500 shadow-xs focus:ring-3 focus:ring-orange-500/50"
                          name="prompt-option"
                          onChange={() => setValue(opt.value)}
                          type="radio"
                        />
                        <span className="ml-3 text-gray-900">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : (
                <TextField
                  label={fieldLabel}
                  name={fieldLabel || 'prompt-field'}
                  onChange={(event) => setValue(event.target.value)}
                  placeholder={placeholder}
                  value={value}
                />
              )}
            </div>
          </div>
        </div>
        <div
          className={cn('px-2 py-4 sm:px-8 flex justify-between bg-gray-100')}
        >
          <div className="sm:w-auto w-1/2">
            <Button
              color="secondary"
              onClick={onCancel}
              size="small"
              variant="outline"
              fullWidth
            >
              {cancelButton}
            </Button>
          </div>
          <div className="ml-3 sm:w-auto w-1/2">
            <Button
              color={color}
              disabled={!value.trim()}
              onClick={() => {
                if (value.trim()) {
                  onSuccess(value!)
                }
              }}
              size="small"
              variant="contained"
              fullWidth
            >
              {okButton}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export const PromptDialogProvider: React.FC<{ children?: React.ReactNode }> = ({
  children
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [confirmationArgs, setConfirmationArgs] =
    useState<PromptDialogArgs | null>(null)
  const [confirmCallbacks, setConfirmationCallbacks] =
    useState<PromptDialogCallbacks | null>(null)
  const value = useMemo(
    () => ({
      prompt: async (args: PromptDialogArgs): Promise<string> => {
        return new Promise((resolve, reject) => {
          setConfirmationArgs(args)
          setConfirmationCallbacks({
            onSuccess: (fieldValue) => {
              setOpen(false)
              resolve(fieldValue)
            },
            onCancel: () => {
              setOpen(false)
              reject()
            }
          })
          setOpen(true)
        })
      },
      close: () => {
        setOpen(false)
      }
    }),
    [setConfirmationArgs, setConfirmationCallbacks, setOpen]
  )
  return (
    <PromptDialogContext.Provider value={value}>
      {children}
      {open && <PromptDialog {...confirmationArgs!} {...confirmCallbacks!} />}
    </PromptDialogContext.Provider>
  )
}
